import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Calendar,
  Download,
  Eye,
  MoreVertical,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FileText,
  CheckCircle2,
  Clock,
  Flame,
  Share2,
  Copy,
  Code2,
  Plus,
  ArrowUp,
} from 'lucide-react';

const NETWORK_SYMBOLS = {
  Ethereum: 'Ξ',
  Polygon: '⬡',
};

const tabs = [
  'Overview',
  'Anchored Records',
  'IPFS Files',
  'Verifications',
  'Nodes',
  'Smart Contracts',
  'Settings',
];

const TAB_ROUTES = {
  overview: '/blockchain-anchoring',
  'anchored-records': '/blockchain-anchoring/anchored-records',
  'ipfs-files': '/blockchain-anchoring/ipfs-files',
  verifications: '/blockchain-anchoring/verifications',
  nodes: '/blockchain-anchoring/nodes',
  'smart-contracts': '/blockchain-anchoring/smart-contracts',
  settings: '/blockchain-anchoring/settings',
};

const DEPLOY_CONTRACT_ROUTE = '/blockchain-anchoring/smart-contracts/deploy';

const summaryCards = [
  { title: 'Total Contracts', value: '12', change: '+20% vs. last month', Icon: FileText, neutral: false },
  { title: 'Active Contracts', value: '10', change: '+25% vs. last month', Icon: CheckCircle2, neutral: false },
  { title: 'Pending Deployment', value: '1', change: '+0% vs. last month', Icon: Clock, neutral: true },
  { title: 'Failed Contracts', value: '1', change: '-50% vs. last month', Icon: Flame, neutral: false, negative: true },
  { title: 'Total Transactions', value: '248', change: '+18% vs. last month', Icon: Share2, neutral: false },
];

const STATUSES = ['All Statuses', 'Active', 'Pending', 'Failed'];
const NETWORKS = ['All Networks', 'Ethereum', 'Polygon', 'BSC'];
const PAGE_SIZE = 10;

const baseContracts = [
  {
    id: '1',
    contractName: 'DocumentSignV1',
    addressShort: '0x7f3a9c4e2b6d...e89f',
    addressFull: '0x7f3a9c4e2b6d8e9f4a1c2b3d4e5f6a7b8c9d0e1f',
    network: 'Ethereum',
    networkType: 'Ethereum (Mainnet)',
    status: 'Active',
    deployedDate: 'May 30, 2025',
    deployedTime: '10:42 AM',
    transactions: '48',
    creatorShort: '0xA1b2c3d4e5f6...7a8b',
    creatorFull: '0xA1b2c3d4e5f67a8b9c0d1e2f3a4b5c6d7e8f9a0b',
    version: 'v1.0.0',
    compiler: 'Solidity ^0.8.21',
    functions: [
      { signature: 'signDocument(address, bytes32)', desc: 'Signs a document hash with authorized key' },
      { signature: 'verifySignature(address, bytes32, bytes)', desc: 'Verifies the signature of a document' },
      { signature: 'getDocumentStatus(bytes32)', desc: 'Returns document status' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.21;

contract DocumentSignV1 {
  mapping(bytes32 => address) public signers;
  mapping(bytes32 => bool) public verified;

  event DocumentSigned(bytes32 indexed docHash, address indexed signer);
  event DocumentVerified(bytes32 indexed docHash, bool status);
}`,
  },
  {
    id: '2',
    contractName: 'IdentityRegistry',
    addressShort: '0x9a2b4c6d7e8f...3a1c',
    addressFull: '0x9a2b4c6d7e8f1a2b3c4d5e6f7a8b9c0d1e2f3a4b',
    network: 'Ethereum',
    networkType: 'Ethereum (Mainnet)',
    status: 'Active',
    deployedDate: 'May 28, 2025',
    deployedTime: '04:15 PM',
    transactions: '32',
    creatorShort: '0xA1b2c3d4e5f6...7a8b',
    creatorFull: '0xA1b2c3d4e5f67a8b9c0d1e2f3a4b5c6d7e8f9a0b',
    version: 'v1.2.0',
    compiler: 'Solidity ^0.8.20',
    functions: [
      { signature: 'registerIdentity(address, string)', desc: 'Registers a new decentralized identity' },
      { signature: 'revokeIdentity(address)', desc: 'Revokes an existing identity' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.20;

contract IdentityRegistry {
  mapping(address => string) public identities;
  event IdentityRegistered(address indexed account, string did);
}`,
  },
  {
    id: '3',
    contractName: 'AccessControl',
    addressShort: '0x5c7e2d9f1b3a...4c6d',
    addressFull: '0x5c7e2d9f1b3a4c6d5e6f7a8b9c0d1e2f3a4b5c6d',
    network: 'Polygon',
    networkType: 'Polygon (PoS)',
    status: 'Active',
    deployedDate: 'May 27, 2025',
    deployedTime: '11:30 AM',
    transactions: '21',
    creatorShort: '0xC3d4e5f6a7b8...1a2b',
    creatorFull: '0xC3d4e5f6a7b89c0d1e2f3a4b5c6d7e8f9a0b1c2d',
    version: 'v2.0.1',
    compiler: 'Solidity ^0.8.24',
    functions: [
      { signature: 'grantRole(bytes32, address)', desc: 'Grants role to account' },
      { signature: 'revokeRole(bytes32, address)', desc: 'Revokes role from account' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.24;

contract AccessControl {
  mapping(bytes32 => mapping(address => bool)) public roles;
  event RoleGranted(bytes32 indexed role, address indexed account);
}`,
  },
  {
    id: '4',
    contractName: 'TimestampVerifier',
    addressShort: '0x3d8f61a1c2e7...9f0a',
    addressFull: '0x3d8f61a1c2e7b9f0a1b2c3d4e5f6a7b8c9d0e1f2',
    network: 'Ethereum',
    networkType: 'Ethereum (Mainnet)',
    status: 'Active',
    deployedDate: 'May 26, 2025',
    deployedTime: '03:21 PM',
    transactions: '18',
    creatorShort: '0xA1b2c3d4e5f6...7a8b',
    creatorFull: '0xA1b2c3d4e5f67a8b9c0d1e2f3a4b5c6d7e8f9a0b',
    version: 'v1.1.0',
    compiler: 'Solidity ^0.8.19',
    functions: [
      { signature: 'anchorTimestamp(bytes32, uint256)', desc: 'Anchors hash with timestamp' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.19;

contract TimestampVerifier {
  mapping(bytes32 => uint256) public timestamps;
  event TimestampAnchored(bytes32 indexed hash, uint256 timestamp);
}`,
  },
  {
    id: '5',
    contractName: 'IPFSRegistry',
    addressShort: '0x8e4c7d2a9f1b...6c3e',
    addressFull: '0x8e4c7d2a9f1b3c5e7a9b0c1d2e3f4a5b6c7d8e9f',
    network: 'Polygon',
    networkType: 'Polygon (PoS)',
    status: 'Active',
    deployedDate: 'May 24, 2025',
    deployedTime: '09:12 AM',
    transactions: '15',
    creatorShort: '0xC3d4e5f6a7b8...1a2b',
    creatorFull: '0xC3d4e5f6a7b89c0d1e2f3a4b5c6d7e8f9a0b1c2d',
    version: 'v1.0.0',
    compiler: 'Solidity ^0.8.21',
    functions: [
      { signature: 'registerCID(string, bytes32)', desc: 'Links IPFS CID to document hash' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.21;

contract IPFSRegistry {
  mapping(bytes32 => string) public ipfsCids;
  event CIDRegistered(bytes32 indexed hash, string cid);
}`,
  },
  {
    id: '6',
    contractName: 'Governance',
    addressShort: '0x6b2f9e8d7c1a...5e4d',
    addressFull: '0x6b2f9e8d7c1a4f3e5a6b7c8d9e0f1a2b3c4d5e6f',
    network: 'Ethereum',
    networkType: 'Ethereum (Mainnet)',
    status: 'Active',
    deployedDate: 'May 22, 2025',
    deployedTime: '02:45 PM',
    transactions: '11',
    creatorShort: '0xA1b2c3d4e5f6...7a8b',
    creatorFull: '0xA1b2c3d4e5f67a8b9c0d1e2f3a4b5c6d7e8f9a0b',
    version: 'v2.1.0',
    compiler: 'Solidity ^0.8.25',
    functions: [
      { signature: 'propose(address[], uint256[], string[], bytes[])', desc: 'Creates governance proposal' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.25;

contract Governance {
  event ProposalCreated(uint256 proposalId, address proposer);
}`,
  },
  {
    id: '7',
    contractName: 'AssetToken',
    addressShort: '0x4d7a9c3e2b6f...8c1d',
    addressFull: '0x4d7a9c3e2b6f1a8c2b3d4e5f6a7b8c9d0e1f2a3b',
    network: 'BSC',
    networkType: 'Binance Smart Chain',
    status: 'Active',
    deployedDate: 'May 20, 2025',
    deployedTime: '05:18 PM',
    transactions: '9',
    creatorShort: '0xD4e5f6a7b8c9...2a3b',
    creatorFull: '0xD4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3',
    version: 'v1.0.0',
    compiler: 'Solidity ^0.8.20',
    functions: [
      { signature: 'mint(address, uint256)', desc: 'Mints new asset tokens' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.20;

contract AssetToken {
  mapping(address => uint256) public balances;
  event Transfer(address indexed from, address indexed to, uint256 value);
}`,
  },
  {
    id: '8',
    contractName: 'ComplianceCheck',
    addressShort: '0x9f1c6e2d4a7b...3d8f',
    addressFull: '0x9f1c6e2d4a7b3e2f1a2b3c4d5e6f7a8b9c0d1e2f',
    network: 'Ethereum',
    networkType: 'Ethereum (Mainnet)',
    status: 'Pending',
    deployedDate: 'May 18, 2025',
    deployedTime: '01:20 PM',
    transactions: '0',
    creatorShort: '0xA1b2c3d4e5f6...7a8b',
    creatorFull: '0xA1b2c3d4e5f67a8b9c0d1e2f3a4b5c6d7e8f9a0b',
    version: 'v0.9.0',
    compiler: 'Solidity ^0.8.21',
    functions: [
      { signature: 'verifyCompliance(address)', desc: 'Runs automated compliance rules' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.21;

contract ComplianceCheck {
  function verifyCompliance(address account) external view returns (bool);
}`,
  },
  {
    id: '9',
    contractName: 'Settlement',
    addressShort: '0x7c3e9a1d5f2b...6e7a',
    addressFull: '0x7c3e9a1d5f2b4c8e1a2b3c4d5e6f7a8b9c0d1e2f',
    network: 'Polygon',
    networkType: 'Polygon (PoS)',
    status: 'Active',
    deployedDate: 'May 15, 2025',
    deployedTime: '04:32 PM',
    transactions: '14',
    creatorShort: '0xC3d4e5f6a7b8...1a2b',
    creatorFull: '0xC3d4e5f6a7b89c0d1e2f3a4b5c6d7e8f9a0b1c2d',
    version: 'v1.1.2',
    compiler: 'Solidity ^0.8.22',
    functions: [
      { signature: 'settleTransaction(bytes32, uint256)', desc: 'Executes financial settlement' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.22;

contract Settlement {
  event Settled(bytes32 indexed txHash, uint256 amount);
}`,
  },
  {
    id: '10',
    contractName: 'AuditLog',
    addressShort: '0x5e9b3c7a1d2f...4b6e',
    addressFull: '0x5e9b3c7a1d2f8f4a1a2b3c4d5e6f7a8b9c0d1e2f',
    network: 'Ethereum',
    networkType: 'Ethereum (Mainnet)',
    status: 'Failed',
    deployedDate: 'May 12, 2025',
    deployedTime: '09:05 AM',
    transactions: '2',
    creatorShort: '0xA1b2c3d4e5f6...7a8b',
    creatorFull: '0xA1b2c3d4e5f67a8b9c0d1e2f3a4b5c6d7e8f9a0b',
    version: 'v1.0.0',
    compiler: 'Solidity ^0.8.21',
    functions: [
      { signature: 'logEvent(string)', desc: 'Logs audit trail' },
    ],
    sourceCode: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.21;

contract AuditLog {
  event LogRecorded(string message);
}`,
  },
];

export default function SmartContracts() {
  const navigate = useNavigate();

  const [activeTab] = useState('smart-contracts');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [networkFilter, setNetworkFilter] = useState('All Networks');
  const [selectedContract, setSelectedContract] = useState(baseContracts[0]);
  const [page, setPage] = useState(1);

  const filteredContracts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return baseContracts.filter((contract) => {
      const matchesSearch =
        !query ||
        contract.contractName.toLowerCase().includes(query) ||
        contract.id.toLowerCase().includes(query) ||
        contract.addressFull.toLowerCase().includes(query) ||
        contract.creatorFull.toLowerCase().includes(query);
      const matchesStatus = statusFilter === 'All Statuses' || contract.status === statusFilter;
      const matchesNetwork = networkFilter === 'All Networks' || contract.network === networkFilter;
      return matchesSearch && matchesStatus && matchesNetwork;
    });
  }, [search, statusFilter, networkFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredContracts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleContracts = filteredContracts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstContract = filteredContracts.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastContract = Math.min(currentPage * PAGE_SIZE, filteredContracts.length);
  const pageButtons = totalPages > 1 ? [1, 2, 3, 4, 5, '...', totalPages] : [1];

  const updateFilter = (setter, value) => {
    setter(value);
    setPage(1);
  };
  const handlePageChange = (nextPage) => setPage(Math.max(1, Math.min(nextPage, totalPages)));

  return (
    <div className="min-h-screen bg-[#f7fbff] text-[#1e3559]">
      {/* Top tabs */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-0">
          <nav className="flex gap-6 overflow-x-auto">
            {tabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(/\s+/g, '-');
              const isActive = activeTab === tabKey;
              return (
                <button
                  type="button"
                  key={tab}
                  onClick={() => navigate(TAB_ROUTES[tabKey])}
                  className={`whitespace-nowrap pb-3 pt-1 text-xs font-medium transition ${
                    isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-900 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </nav>

          <button
            type="button"
            onClick={() => navigate(DEPLOY_CONTRACT_ROUTE)}
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-[#6200ee] px-3 text-[10px] font-medium text-white shadow-sm transition hover:bg-[#5100c9]"
          >
            <Plus className="h-3.5 w-3.5" />
            Deploy Contract
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-4 px-0 pb-5">
        {/* Summary cards */}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {summaryCards.map((card) => {
            const Icon = card.Icon;

            let changeColor = 'text-[#21a675]';
            if (card.neutral) {
              changeColor = 'text-[#7b91b0]';
            } else if (card.negative) {
              changeColor = 'text-rose-600';
            }

            return (
              <div
                key={card.title}
                className="min-w-0 rounded-md border border-[#e5edf7] bg-white px-3 py-2 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f2edff]">
                    <Icon className="h-7 w-7 text-[#6426df]" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-[11px] font-medium text-[#7290b5] leading-tight">{card.title}</p>
                    <p className="text-xl font-bold leading-tight text-[#20375e]">{card.value}</p>
                    <p className={`text-[9px] leading-tight ${changeColor}`}>
                      {!card.neutral && <ArrowUp className="h-3 w-3 inline" strokeWidth={2} />}
                      {card.change}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Table + right details sidebar */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px]">
          {/* Table card */}
          <div className="min-w-0 overflow-hidden rounded-md border border-[#e5edf7] bg-white shadow-sm">
            {/* Filters bar */}
            <div className="flex flex-wrap items-center gap-1.5 border-b border-[#edf2f8] bg-[#f8fbff] px-3 py-2">
              <div className="relative min-w-[160px] flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-[#86a0be]" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => updateFilter(setSearch, e.target.value)}
                  placeholder="Search by contract name, address, or creator..."
                  className="h-6 w-full rounded border border-[#dfe9f4] bg-white pl-6 pr-2 text-[10px] text-[#49678f] outline-none placeholder:text-[#91a5bf] focus:border-[#8d62ea]"
                />
              </div>
              <div className="relative">
                <select value={statusFilter} onChange={(e) => updateFilter(setStatusFilter, e.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {STATUSES.map((status) => <option key={status}>{status}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>
              <div className="relative">
                <select value={networkFilter} onChange={(e) => updateFilter(setNetworkFilter, e.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {NETWORKS.map((net) => <option key={net}>{net}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>
              <button type="button" className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] text-[#5b769c] hover:bg-white">
                <Calendar className="h-3 w-3 text-[#718daf]" />
                May 1 – May 31, 2025
                <ChevronDown className="h-2.5 w-2.5 text-[#87a0be]" />
              </button>
              <button type="button" className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] font-medium text-[#6426df] hover:bg-violet-50">
                <Download className="h-3 w-3" />
                Export
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full table-fixed border-collapse">
                <colgroup>
                  <col className="w-[42px]" />
                  <col className="w-[160px]" />
                  <col className="w-[150px]" />
                  <col className="w-[100px]" />
                  <col className="w-[90px]" />
                  <col className="w-[120px]" />
                  <col className="w-[90px]" />
                  <col className="w-[75px]" />
                </colgroup>
                <thead>
                  <tr className="h-9 border-b border-[#edf2f8] bg-[#f8fbff] text-left text-[10px] font-semibold uppercase tracking-wide text-[#7791b3]">
                    <th className="w-[42px] min-w-[42px] px-3 text-center">#</th>
                    <th className="px-2">Contract Name</th>
                    <th className="px-2">Address</th>
                    <th className="px-2">Network</th>
                    <th className="px-2">Status</th>
                    <th className="px-2">Deployed On</th>
                    <th className="px-2">Transactions</th>
                    <th className="w-[75px] min-w-[75px] px-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleContracts.map((contract, index) => {
                    const isSelected = selectedContract?.id === contract.id;
                    return (
                      <tr
                        key={contract.id}
                        onClick={() => setSelectedContract(contract)}
                        className={`h-[49px] cursor-pointer border-b border-[#f0f4f8] transition hover:bg-[#f9fbff] ${isSelected ? 'bg-[#fbfaff]' : 'bg-white'}`}
                      >
                        <td className="w-[42px] min-w-[42px] px-3 text-center text-xs font-medium text-[#58749c]">
                          {(currentPage - 1) * PAGE_SIZE + index + 1}
                        </td>
                        <td className="px-2">
                          <p className="truncate text-xs font-medium text-[#46658e]" title={contract.contractName}>{contract.contractName}</p>
                        </td>
                        <td className="px-2">
                          <p title={contract.addressFull} className="truncate font-mono text-[11px] text-[#6f88a8]">{contract.addressShort}</p>
                        </td>
                        <td className="px-2">
                          <div className="flex items-center gap-1.5">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-700">
                              {NETWORK_SYMBOLS[contract.network] ?? 'B'}
                            </span>
                            <span className="truncate text-xs text-[#56729a]">{contract.network}</span>
                          </div>
                        </td>
                        <td className="px-2">
                          <StatusBadge status={contract.status} />
                        </td>
                        <td className="px-2">
                          <p className="text-xs leading-4 text-[#56729a]">{contract.deployedDate}</p>
                          <p className="text-[11px] leading-4 text-[#8098b6]">{contract.deployedTime}</p>
                        </td>
                        <td className="px-2">
                          <p className="text-xs font-medium text-[#56729a]">{contract.transactions}</p>
                        </td>
                        <td className="w-[75px] min-w-[75px] px-2">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedContract(contract); }}
                              title="View contract details"
                              className="text-[#7c9fca] transition hover:text-[#6426df]"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              title="More actions"
                              className="text-[#8ca4c2] transition hover:text-[#6426df]"
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleContracts.length === 0 && (
                    <tr>
                      <td colSpan={8} className="h-72 text-center text-xs text-[#8ca2bd]">No contracts found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-3 border-t border-[#edf2f8] px-3 py-3">
              <p className="text-[9px] text-[#6682aa]">
                Showing {firstContract}-{lastContract} of {filteredContracts.length} contracts
              </p>
              <nav aria-label="Smart contracts pagination" className="flex items-center gap-1">
                <PaginationButton disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} label="Previous page">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </PaginationButton>
                {pageButtons.map((item) =>
                  item === '...' ? (
                    <span key="ellipsis" className="flex h-7 w-7 items-center justify-center rounded border border-[#e2eaf4] text-[9px] text-[#7690b2]">...</span>
                  ) : (
                    <PaginationButton key={item} active={currentPage === item} onClick={() => handlePageChange(item)} label={`Page ${item}`}>
                      {item}
                    </PaginationButton>
                  )
                )}
                <PaginationButton disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} label="Next page">
                  <ChevronRight className="h-3.5 w-3.5" />
                </PaginationButton>
              </nav>
            </div>
          </div>

          {/* Right-side contract details */}
          {selectedContract && (
            <ContractDetails
              contract={selectedContract}
              onClose={() => setSelectedContract(null)}
            />
          )}
        </section>
      </main>
    </div>
  );
}

function PaginationButton({ children, onClick, disabled = false, active = false, label }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`flex h-7 w-7 items-center justify-center rounded border text-[9px] font-medium transition ${
        active
          ? 'border-[#6200ee] bg-[#6200ee] text-white'
          : 'border-[#e2eaf4] bg-white text-[#718caf] hover:bg-[#f7faff]'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Active: 'bg-[#dff7ea] text-[#29996e]',
    Pending: 'bg-amber-50 text-amber-600',
    Failed: 'bg-rose-50 text-rose-600',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      {status === 'Active' && <CheckCircle2 className="h-2.5 w-2.5" />}
      {status}
    </span>
  );
}

function DetailRow({ label, value, children, link = false, compact = false }) {
  return (
    <div className="grid grid-cols-[90px_minmax(0,1fr)] items-start gap-2">
      <span className="text-[11px] leading-[15px] text-[#8198b7]">{label}</span>
      {children ? (
        <div className="min-w-0">{children}</div>
      ) : (
        <div className="flex min-w-0 items-start gap-1">
          <span
            title={value}
            className={`min-w-0 break-all leading-[15px] ${compact ? 'font-mono text-[10px]' : 'text-[11px]'} ${link ? 'text-[#6426df]' : 'text-[#55719a]'}`}
          >
            {value}
          </span>
          {link && <ExternalLink className="mt-0.5 h-2.5 w-2.5 shrink-0 text-[#6426df]" />}
        </div>
      )}
    </div>
  );
}

function ContractDetails({ contract, onClose }) {
  return (
    <aside className="min-w-0 rounded-md border border-[#e5edf7] bg-white p-2.5 shadow-sm space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-bold text-[#29446c]">Contract Details</h2>
        <StatusBadge status={contract.status} />
      </div>

      {/* Contract Identity */}
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#f2edff]">
          <Code2 className="h-3.5 w-3.5 text-[#6426df]" />
        </div>
        <div className="min-w-0">
          <h3 title={contract.contractName} className="truncate text-[11px] font-semibold text-[#29446c]">{contract.contractName}</h3>
          <p className="mt-0.5 text-[10px] text-[#8299b8]">Smart Contract</p>
        </div>
      </div>

      {/* Core metadata */}
      <div className="space-y-1.5 pt-1">
        <DetailRow label="Contract Address" value={contract.addressFull} link compact />
        <DetailRow label="Network" value={contract.networkType} />
        <DetailRow label="Creator" value={contract.creatorFull} link compact />
        <DetailRow label="Deployed On" value={`${contract.deployedDate}, ${contract.deployedTime}`} />
        <DetailRow label="Version" value={contract.version} />
        <DetailRow label="Compiler" value={contract.compiler} />
      </div>

      {/* Contract Functions */}
      <div className="border-t border-[#eaf0f7] pt-2">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-[10px] font-semibold text-[#29446c]">Contract Functions</h3>
          <ChevronDown className="h-3 w-3 text-[#8198b7]" />
        </div>
        <div className="space-y-1.5">
          {contract.functions.map((fn) => (
            <div key={fn.signature} className="rounded border border-[#edf2f8] bg-[#f8fbff] p-2">
              <p className="font-mono text-[10px] font-semibold text-[#6426df]">{fn.signature}</p>
              <p className="mt-0.5 text-[10px] text-[#718daf]">{fn.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contract Source Code */}
      <div className="border-t border-[#eaf0f7] pt-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-semibold text-[#29446c]">Contract Source Code</span>
          <button type="button" className="inline-flex items-center gap-1 text-[10px] text-[#6426df] hover:underline">
            View on Etherscan <ExternalLink className="h-2.5 w-2.5" />
          </button>
        </div>
        <div className="rounded border border-[#1e293b] bg-[#0f172a] p-2 text-[9px] font-mono text-slate-200 overflow-x-auto max-h-32">
          <div className="flex items-center justify-between mb-1.5 pb-1 border-b border-slate-800 text-slate-400">
            <span>Solidity</span>
            <button type="button" title="Copy code" className="hover:text-white">
              <Copy className="h-3 w-3" />
            </button>
          </div>
          <pre className="whitespace-pre">{contract.sourceCode}</pre>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-3 gap-1 pt-2">
        <button
          type="button"
          className="flex h-6 items-center justify-center rounded border border-[#a987ed] bg-white px-1 text-[9px] font-medium text-[#6426df] transition hover:bg-[#f7f2ff]"
        >
          Verify Contract
        </button>
        <button
          type="button"
          className="flex h-6 items-center justify-center rounded border border-[#a987ed] bg-white px-1 text-[9px] font-medium text-[#6426df] transition hover:bg-[#f7f2ff]"
        >
          View Logs
        </button>
        <button
          type="button"
          className="flex h-6 items-center justify-center rounded border border-[#a987ed] bg-white px-1 text-[9px] font-medium text-[#6426df] transition hover:bg-[#f7f2ff]"
        >
          Download ABI
        </button>
      </div>
    </aside>
  );
}